import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Divider } from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import MUIDataTable from 'mui-datatables';
import { TextFormattedNumber } from 'components';
import { getStoreOrderItemsDetailsApi } from 'api';
import OrderItemDetails from './OrderItemDetails';
import SurveyResults from './SurveyResults';


const useStyles = makeStyles({
  inheritTable: {
    '& .MuiTableCell-root': {
      fontSize: '12px',
      padding: '12px 8px',
      backgroundColor: '#E7F0FF',
      verticalAlign: 'top',
      '&:first-child': {
        paddingLeft: '20px'
      },
      '& > div': {
        fontSize: '12px'
      }
    },
    '&.MuiPaper-root': {
      backgroundColor: '#E7F0FF'
    },
    '& .MuiTableCell-head': {
      padding: '12px 18px',
      whiteSpace: 'nowrap',
      '&.MuiTableCell-root:first-child': {
        paddingLeft: '28px'
      }
    }
  },
  nameCell: {
    width: '20%'
  },
  btnText: {
    color: '#434343',
    fontSize: '12px'
  },
  totals: {
    '& > div > div:nth-child(odd):not(.grand-total)': {
      backgroundColor: '#f0f8ff'
    }
  },
  alertIcon: {
    '& .MuiAlert-icon .MuiSvgIcon-root.MuiSvgIcon-fontSizeInherit': {
      fill: '#FFF'
    },
  },
  borderTopBold: {
    borderTop: '2px solid #D0E9FF'
  }
});

const options = {
  selectableRows: 'none',
  download: false,
  search: false,
  print: false,
  filter: false,
  viewColumns: false,
  pagination: false,
  sort: false,
  elevation: 0
};

const OrderedItems = ({order}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    items,
    entity_id,
    increment_id,
    subtotal,
    extension_attributes,
    extension_attributes: {
      mp_reward: {
        mp_reward_earn,
        mp_reward_discount,
        mp_reward_spent
      },
      fooman_total_group
    },
    discount_amount,
    base_discount_invoiced,
    tax_amount,
    shipping_amount,
    grand_total,
    total_paid,
    total_due
  } = order;

  const {
    survey_question,
    survey_question_value
  } = extension_attributes || {
    survey_question: null,
    survey_question_value: null
  };

  const quoteDiscount = Math.abs(base_discount_invoiced) - (Math.abs(discount_amount) || 0);

  const {
    orderItemsDetails
  } = useSelector((state: IReduxState) => state.store_orders);

  useEffect(() => {
    if (!orderItemsDetails[entity_id]) {
      getStoreOrderItemsDetailsApi({dispatch, entityId: entity_id});
    }
  }, [entity_id]);

  const columns = [
    {
      name: 'name',
      label: 'Products',
      options: {
        setCellHeaderProps: () => ({style: {width: '20%'}}),
        customBodyRenderLite: (dataIndex) => {
          const { name } = items[dataIndex];

          return (
            <>
              <div>{name}</div>
              <OrderItemDetails
                item={items[dataIndex]}
                itemDetails={orderItemsDetails[entity_id]?.[dataIndex]}
              />
            </>
          );
        }
      }
    },
    {
      name: 'price',
      label: 'Price',
      options: {
        customBodyRenderLite: (dataIndex) => {
          const {price} = items[dataIndex];

          return (
            <TextFormattedNumber
              value={price}
              prefix="$"
            />
          );
        }
      }
    },
    {
      name: 'qty',
      label: 'Quantity',
      options: {
        customBodyRenderLite: (dataIndex) => {
          const {
            qty_ordered,
            qty_invoiced,
            qty_refunded,
            qty_shipped,
            qty_canceled,
            product_type
          } = items[dataIndex];
          return (
            <>
              <div>{`Ordered ${qty_ordered}`}</div>
              <div>{`Invoiced ${qty_invoiced}`}</div>
              {!!qty_refunded &&
              <div>{`Refunded ${qty_refunded}`}</div>
              }
              {!!qty_shipped && product_type === 'simple' &&
              <div>{`Shipped ${qty_shipped}`}</div>
              }
              {!!qty_canceled &&
              <div>{`Canceled ${qty_canceled}`}</div>
              }
            </>
          );
        }
      }
    },
    {
      name: 'row_total',
      label: 'Subtotal',
      options: {
        customBodyRenderLite: (dataIndex) => {
          const {row_total} = items[dataIndex];

          return (
            <TextFormattedNumber
              value={row_total}
              prefix="$"
            />
          );
        }
      }
    },
    {
      name: 'tax_amount',
      label: 'Tax Amount',
      options: {
        customBodyRenderLite: (dataIndex) => {
          const {tax_amount} = items[dataIndex];

          return (
            <TextFormattedNumber
              value={tax_amount}
              prefix="$"
            />
          );
        }
      }
    },
    {
      name: 'tax_percent',
      label: 'Tax Percent',
      options: {
        customBodyRenderLite: (dataIndex) => {
          const {tax_percent} = items[dataIndex];

          return (
            <TextFormattedNumber
              value={tax_percent}
              suffix="%"
            />
          );
        }
      }
    },
    {
      name: 'discount_amount',
      label: 'Discount',
      options: {
        customBodyRenderLite: (dataIndex) => {
          const {discount_amount} = items[dataIndex];

          return (
            <TextFormattedNumber
              value={discount_amount}
              prefix="$"
            />
          );
        }
      }
    },
    {
      name: 'row_total_incl_tax',
      label: 'Total',
      options: {
        customBodyRenderLite: (dataIndex) => {
          const {row_total, tax_percent, discount_amount} = items[dataIndex];
          const total = (row_total - discount_amount) * (1 + (tax_percent / 100));
          return (
            <TextFormattedNumber
              value={total}
              prefix="$"
            />
          );
        }
      }
    }
  ];

  return (
    <div>
      <MUIDataTable
        className={classes.inheritTable}
        data={items}
        columns={columns}
        options={options}
      />
      {survey_question && survey_question_value &&
        <SurveyResults
          question={survey_question}
          answer={survey_question_value}
          classes={classes}
        />
      }
      <div
        className={`pt-4 pb-1 px-6 h-full text-12 flex-column items-end ${classes.totals}`}
      >
        <h6 className="text-primary mb-4 max-w-600 w-full uppercase">Totals</h6>
        <div className="max-w-600 w-full">
          <div className="flex items-center justify-between p-2">
            <div>Subtotal</div>
            <div>
              <TextFormattedNumber
                value={subtotal}
                prefix="$"
              />
            </div>
          </div>
          {!!mp_reward_earn &&
          <div className="flex items-center justify-between p-2">
            <div>Earned</div>
            <div>
              {`${mp_reward_earn} points`}
            </div>
          </div>}
          {!!mp_reward_spent &&
          <div className="flex items-center justify-between p-2">
            <div>Spend</div>
            <div>
              {`${mp_reward_spent} points`}
            </div>
          </div>}
          {!!extension_attributes?.credit &&
          <div className="flex items-center justify-between p-2">
            <div>Credit</div>
            <div>
              <TextFormattedNumber
                value={extension_attributes.credit}
                prefix="$"
              />
            </div>
          </div>}
          {!!mp_reward_discount &&
          <div className="flex items-center justify-between p-2">
            <div>Spend Points</div>
            <div>
              <TextFormattedNumber
                value={mp_reward_discount}
                prefix="-$"
              />
            </div>
          </div>}
          {!!quoteDiscount &&
          <div className="flex items-center justify-between p-2">
            <div>Quote Discount</div>
            <div>
              <TextFormattedNumber
                value={quoteDiscount}
                prefix="$"
              />
            </div>
          </div>}
          {!!discount_amount && !!order?.discount_description &&
          <div className="flex items-center justify-between p-2">
            <div>{`Discount ${!!order?.discount_description ? `(${order.discount_description})` : ''}`}</div>
            <div>
              <TextFormattedNumber
                value={Math.abs(discount_amount)}
                prefix="$"
              />
            </div>
          </div>}
          <div className="flex items-center justify-between p-2">
            <div>Shipping & Handling</div>
            <div>
              <TextFormattedNumber
                value={shipping_amount}
                prefix="$"
              />
            </div>
          </div>
          {!!extension_attributes?.gift_card_value && extension_attributes?.gift_card_code &&
          <div className="flex items-center justify-between p-2">
            <div>{`Gift Cards (${extension_attributes.gift_card_code})`}</div>
            <div>
              <TextFormattedNumber
                value={extension_attributes.gift_card_value}
                prefix="$"
              />
            </div>
          </div>}
          {!!tax_amount &&
          <div className="flex items-center justify-between p-2">
            <div>Tax</div>
            <div>
              <TextFormattedNumber
                value={tax_amount}
                prefix="$"
              />
            </div>
          </div>}
          {!!fooman_total_group?.items?.length &&
          fooman_total_group?.items?.map((fooman) => {
            return (
              <div key={fooman.code} className="flex items-center justify-between p-2">
                <div>{fooman.label}</div>
                <div>
                  <TextFormattedNumber
                    value={fooman.amount}
                    prefix="$"
                  />
                </div>
              </div>
            );
          })}
          <Divider className="my-2" />
          <div className="flex items-center justify-between p-2 mb-2 text-14 font-bold grand-total">
            <div>
              {'Grand Total'}
            </div>
            <div>
              <TextFormattedNumber
                value={grand_total}
                prefix="$"
              />
            </div>
          </div>
          <div className="flex items-center justify-between p-2 mb-2 text-14 font-bold grand-total">
            <div>
              {'Total Paid '}
              {total_due > 0 && (
                <>
                  {'('}
                  <Link className="text-primary underline" to={{ pathname: '/deposits', state: { order_id: increment_id } }}>
                    View details
                  </Link>
                  {')'}
                </>
              )}
            </div>
            <div>
              <TextFormattedNumber value={total_paid} prefix="$" />
            </div>
          </div>
          <div className="flex items-center justify-between p-2 mb-2 text-14 font-bold grand-total">
            <div>
              {'Total Refunded'}
            </div>
            <div>
              <TextFormattedNumber
                value={order?.total_refunded || 0}
                prefix="$"
              />
            </div>
          </div>
          <div className="flex items-center justify-between p-2 mb-2 text-14 font-bold grand-total">
            <div>
              {'Total Due'}
            </div>
            <div>
              <TextFormattedNumber
                value={total_due}
                prefix="$"
              />
            </div>
          </div>
          {!!order?.coupon_code &&
            <div className="flex items-center justify-between p-2 mb-2 text-14 font-bold grand-total">
              <div>
                {'Coupon applied'}
              </div>
              <div>
                {order.coupon_code}
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  );
};

export default OrderedItems;
